import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { ImageGallery } from '../components/ImageGallery';
import { EnergyEfficiencyScale } from '../components/EnergyEfficiencyScale';
import {
    MapPin, Ruler, Bed, Bathtub, Tag, Calendar, CurrencyEur,
    CaretLeft, Phone, EnvelopeSimple, Buildings, User, PaperPlaneTilt,
    CheckCircle, Lightning, Thermometer, House
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnung', house: 'Haus', land: 'Grundstück', office: 'Büro',
    retail: 'Einzelhandel', warehouse: 'Lager/Halle', other: 'Sonstige'
};

const CONDITION_LABELS = {
    ERSTBEZUG: 'Erstbezug', NEUWERTIG: 'Neuwertig', MODERNISIERT: 'Modernisiert',
    GEPFLEGT: 'Gepflegt', RENOVIERUNGSBEDUERFTIG: 'Renovierungsbedürftig',
    ABBRUCHREIF: 'Abbruchreif', NACH_VEREINBARUNG: 'Nach Vereinbarung'
};

const ENERGY_CERT_LABELS = { VERBRAUCH: 'Verbrauchsausweis', BEDARF: 'Bedarfsausweis' };

function formatPrice(price, currency = 'EUR') {
    if (!price) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(price);
}

function formatMainPrice(price, currency, marketingType) {
    if (!price) return marketingType === 'rent' ? 'Miete auf Anfrage' : 'Kaufpreis auf Anfrage';
    return formatPrice(price, currency);
}

export default function PublicPropertyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

    const fetchProperty = useCallback(async () => {
        try {
            const [propRes, contactRes] = await Promise.all([
                axios.get(`${API}/api/public/properties/${id}`),
                axios.get(`${API}/api/public/properties/${id}/contact`)
            ]);
            setProperty(propRes.data);
            setContact(contactRes.data);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchProperty(); }, [fetchProperty]);

    // Auto-resize für iframe-Embed (wenn Detail-Seite aus dem Embed-Widget heraus geöffnet wird)
    useEffect(() => {
        const sendHeight = () => {
            const h = document.body.scrollHeight;
            try {
                window.parent.postMessage({ type: 'immo-embed-resize', height: h }, '*');
            } catch (e) { /* nicht im iframe */ }
        };
        const observer = new ResizeObserver(sendHeight);
        observer.observe(document.body);
        sendHeight();
        return () => observer.disconnect();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Bitte Name, E-Mail und Nachricht ausfüllen');
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/api/public/inquiries`, {
                property_id: id,
                ...form
            });
            setSent(true);
            toast.success('Anfrage erfolgreich gesendet!');
        } catch (error) {
            toast.error('Fehler beim Senden der Anfrage');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
    }
    if (!property) {
        return <div className="flex flex-col items-center justify-center min-h-screen gap-4"><p className="text-lg text-muted-foreground">Immobilie nicht gefunden</p></div>;
    }

    const address = property.address || {};
    const addressStr = [address.zip, address.city].filter(Boolean).join(' ') || 'Keine Adresse';

    return (
        <div className="min-h-screen bg-background" data-testid="public-detail-page">
            {/* Header */}
            <div className="bg-card border-b">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-sm" data-testid="back-btn">
                        <CaretLeft size={16} className="mr-1" /> Zurück
                    </Button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Gallery */}
                {property.images && property.images.length > 0 && (
                    <div className="mb-6">
                        <ImageGallery images={property.images} propertyTitle={property.title} />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Title & Price */}
                        <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge variant="outline" className="rounded-sm">{PROPERTY_TYPE_LABELS[property.property_type] || property.property_type}</Badge>
                                <Badge variant="outline" className="rounded-sm">{property.marketing_type === 'rent' ? 'Miete' : 'Kauf'}</Badge>
                                {(property.object_nr_external || property.external_id) && (
                                    <Badge className="rounded-sm bg-foreground text-background font-mono" data-testid="objnr-badge">
                                        Obj.-Nr. {property.object_nr_external || property.external_id}
                                    </Badge>
                                )}
                            </div>
                            <h1 className="text-2xl font-bold tracking-tight mb-1" data-testid="property-title">{property.title}</h1>
                            {addressStr && <p className="text-muted-foreground flex items-center gap-1"><MapPin size={14} />{addressStr}</p>}
                            <p className="text-2xl font-bold text-primary mt-3" data-testid="property-price">
                                {formatMainPrice(property.price, property.currency, property.marketing_type)}
                                {property.marketing_type === 'rent' && !!property.price && <span className="text-sm font-normal text-muted-foreground"> / Monat</span>}
                            </p>
                        </div>

                        {/* Key Facts */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {property.area && <div className="bg-muted/50 rounded-sm p-3 text-center"><Ruler size={18} className="mx-auto mb-1 text-muted-foreground" /><p className="text-sm text-muted-foreground">Wohnfläche</p><p className="font-semibold">{property.area} m²</p></div>}
                            {property.rooms && <div className="bg-muted/50 rounded-sm p-3 text-center"><Bed size={18} className="mx-auto mb-1 text-muted-foreground" /><p className="text-sm text-muted-foreground">Zimmer</p><p className="font-semibold">{property.rooms}</p></div>}
                            {property.year_built && <div className="bg-muted/50 rounded-sm p-3 text-center"><Calendar size={18} className="mx-auto mb-1 text-muted-foreground" /><p className="text-sm text-muted-foreground">Baujahr</p><p className="font-semibold">{property.year_built}</p></div>}
                            {property.energy_efficiency_class && <div className="bg-muted/50 rounded-sm p-3 text-center"><Lightning size={18} className="mx-auto mb-1 text-muted-foreground" /><p className="text-sm text-muted-foreground">Energieklasse</p><p className="font-semibold">{property.energy_efficiency_class}</p></div>}
                        </div>

                        {/* Description */}
                        {property.description && (
                            <Card className="border">
                                <CardHeader><CardTitle className="text-base">Beschreibung</CardTitle></CardHeader>
                                <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{property.description}</p></CardContent>
                            </Card>
                        )}

                        {/* Equipment Description */}
                        {property.equipment_description && (
                            <Card className="border" data-testid="equipment-card">
                                <CardHeader><CardTitle className="text-base">Ausstattung</CardTitle></CardHeader>
                                <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{property.equipment_description}</p></CardContent>
                            </Card>
                        )}

                        {/* Other Info */}
                        {property.other_description && (
                            <Card className="border" data-testid="other-info-card">
                                <CardHeader><CardTitle className="text-base">Sonstiges</CardTitle></CardHeader>
                                <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{property.other_description}</p></CardContent>
                            </Card>
                        )}

                        {/* Areas */}
                        {(property.area || property.plot_area || property.total_area) && (
                            <Card className="border">
                                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Ruler size={18} />Flächen</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        {property.area && <div><p className="text-muted-foreground">Wohnfläche</p><p className="font-medium">{property.area} m²</p></div>}
                                        {property.plot_area && <div><p className="text-muted-foreground">Grundstück</p><p className="font-medium">{property.plot_area} m²</p></div>}
                                        {property.total_area && <div><p className="text-muted-foreground">Gesamtfläche</p><p className="font-medium">{property.total_area} m²</p></div>}
                                        {property.usable_area && <div><p className="text-muted-foreground">Nutzfläche</p><p className="font-medium">{property.usable_area} m²</p></div>}
                                        {property.balcony_terrace_area && <div><p className="text-muted-foreground">Balkon/Terrasse</p><p className="font-medium">{property.balcony_terrace_area} m²</p></div>}
                                        {property.num_parking_spaces && <div><p className="text-muted-foreground">Stellplätze</p><p className="font-medium">{property.num_parking_spaces}</p></div>}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Prices */}
                        {(property.commission || property.rental_income || property.rental_income_target || property.additional_costs) && (
                            <Card className="border">
                                <CardHeader><CardTitle className="text-base flex items-center gap-2"><CurrencyEur size={18} />Preise & Kosten</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        {property.commission && <div><p className="text-muted-foreground">Provision</p><p className="font-medium">{property.commission}</p></div>}
                                        {property.rental_income && <div><p className="text-muted-foreground">Ist-Mieteinnahmen p.a.</p><p className="font-medium">{formatPrice(property.rental_income)}</p></div>}
                                        {property.rental_income_target && <div><p className="text-muted-foreground">Soll-Mieteinnahmen p.a.</p><p className="font-medium">{formatPrice(property.rental_income_target)}</p></div>}
                                        {property.additional_costs && <div><p className="text-muted-foreground">Nebenkosten</p><p className="font-medium">{formatPrice(property.additional_costs)}</p></div>}
                                        {property.warm_rent && <div><p className="text-muted-foreground">Warmmiete</p><p className="font-medium">{formatPrice(property.warm_rent)}</p></div>}
                                        {property.heating_costs && <div><p className="text-muted-foreground">Heizkosten</p><p className="font-medium">{formatPrice(property.heating_costs)}</p></div>}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Location */}
                        {property.location_description && (
                            <Card className="border">
                                <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={18} />Lage</CardTitle></CardHeader>
                                <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{property.location_description}</p></CardContent>
                            </Card>
                        )}

                        {/* Energy Certificate - Visual Scale */}
                        {(property.energy_certificate_type || property.energy_efficiency_class || property.energy_value || property.energy_end_demand) && (
                            <Card className="border" data-testid="energy-certificate-card">
                                <CardContent className="pt-6">
                                    <EnergyEfficiencyScale
                                        energyClass={property.energy_efficiency_class}
                                        energyValue={property.energy_end_demand || property.energy_value}
                                        certificateType={property.energy_certificate_type}
                                        validUntil={property.energy_certificate_valid_until}
                                        condition={CONDITION_LABELS[property.condition] || property.condition}
                                        yearBuilt={property.year_built}
                                    />
                                    {/* Additional energy details */}
                                    {(property.energy_carrier || property.heating_type || property.energy_with_hot_water !== null) && (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 mt-3 border-t text-sm">
                                            {property.energy_carrier && (
                                                <div>
                                                    <p className="text-muted-foreground text-xs">Energieträger</p>
                                                    <p className="font-medium capitalize">{property.energy_carrier}</p>
                                                </div>
                                            )}
                                            {property.heating_type && (
                                                <div>
                                                    <p className="text-muted-foreground text-xs">Heizungsart</p>
                                                    <p className="font-medium capitalize">{property.heating_type}</p>
                                                </div>
                                            )}
                                            {property.energy_with_hot_water !== null && property.energy_with_hot_water !== undefined && (
                                                <div>
                                                    <p className="text-muted-foreground text-xs">Warmwasser enthalten</p>
                                                    <p className="font-medium">{property.energy_with_hot_water ? 'Ja' : 'Nein'}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar - Contact */}
                    <div className="space-y-6">
                        {/* Contact Info Card */}
                        {contact && contact.type !== 'none' && (
                            <Card className="border" data-testid="contact-info-card">
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        {contact.type === 'broker' ? <User size={18} weight="fill" /> : <Buildings size={18} weight="fill" />}
                                        {contact.type === 'broker' ? 'Ihr Ansprechpartner' : 'Kontakt'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        {contact.avatar ? (
                                            <img
                                                src={contact.avatar.startsWith('http') ? contact.avatar : `${API}/api/storage/${contact.avatar}`}
                                                alt={contact.name}
                                                className="w-14 h-14 rounded-full object-cover border border-border shrink-0"
                                                data-testid="contact-avatar"
                                            />
                                        ) : (
                                            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center border border-border shrink-0" data-testid="contact-avatar-placeholder">
                                                <User size={24} className="text-muted-foreground" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-semibold" data-testid="contact-name">{contact.name}</p>
                                            {contact.company && <p className="text-sm text-muted-foreground">{contact.company}</p>}
                                        </div>
                                    </div>
                                    <Separator />
                                    {contact.phone && (
                                        <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                                            <Phone size={14} />{contact.phone}
                                        </a>
                                    )}
                                    {contact.email && (
                                        <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                                            <EnvelopeSimple size={14} />{contact.email}
                                        </a>
                                    )}
                                    {contact.address && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} />{contact.address}</p>}
                                </CardContent>
                            </Card>
                        )}

                        {/* Inquiry Form */}
                        <Card className="border" data-testid="inquiry-form-card">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <EnvelopeSimple size={18} weight="fill" />
                                    Kontaktanfrage
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {sent ? (
                                    <div className="text-center py-6" data-testid="inquiry-success">
                                        <CheckCircle size={48} weight="fill" className="mx-auto mb-3 text-green-500" />
                                        <p className="font-semibold text-lg">Anfrage gesendet!</p>
                                        <p className="text-sm text-muted-foreground mt-1">Wir melden uns schnellstmöglich bei Ihnen.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-3">
                                        <div>
                                            <Label className="text-xs">Name *</Label>
                                            <Input
                                                value={form.name}
                                                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                                                placeholder="Max Mustermann"
                                                className="rounded-sm mt-1"
                                                required
                                                data-testid="inquiry-name"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">E-Mail *</Label>
                                            <Input
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                                                placeholder="max@example.com"
                                                className="rounded-sm mt-1"
                                                required
                                                data-testid="inquiry-email"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Telefon</Label>
                                            <Input
                                                value={form.phone}
                                                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                                                placeholder="+49 123 456789"
                                                className="rounded-sm mt-1"
                                                data-testid="inquiry-phone"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Nachricht *</Label>
                                            <textarea
                                                value={form.message}
                                                onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                                                placeholder="Ich interessiere mich für diese Immobilie..."
                                                className="w-full mt-1 min-h-[100px] rounded-sm border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                required
                                                data-testid="inquiry-message"
                                            />
                                        </div>
                                        <Button type="submit" className="w-full rounded-sm" disabled={sending} data-testid="inquiry-submit">
                                            {sending ? 'Wird gesendet...' : (
                                                <><PaperPlaneTilt size={16} className="mr-2" />Anfrage senden</>
                                            )}
                                        </Button>
                                    </form>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
